/*
 * Counter Windows / Chrome Scale so the admin UI matches Scale 100%.
 * Same look from Chrome/Windows ~75% through 150% — zoom = 1 / nativeDpr.
 *
 * Chrome pitfall: while html { zoom: 1/dpr } is applied, devicePixelRatio
 * often reports ~1. Recover native dpr as reported / appliedZoom when
 * CSS zoom mutates dpr; otherwise use reported as-is.
 *
 * Jump avoidance: never clear zoom to measure; skip style writes when the
 * target zoom is unchanged; calibrate only once (no second zoom pass).
 */

// Chrome zoom presets + Windows scales (0.8 kept distinct from 0.75)
const SCALE_STEPS = [0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.25, 2.5];
const SNAP_EPSILON = 0.03;

let appliedZoom = 1;
/** @type {boolean|null} */
let zoomAffectsDpr = null;
let rafId = 0;

const snapDpr = (dpr) => {
    let best = dpr;
    let bestDiff = Infinity;
    for (let i = 0; i < SCALE_STEPS.length; i += 1) {
        const step = SCALE_STEPS[i];
        const diff = Math.abs(dpr - step);
        if (diff < bestDiff) {
            bestDiff = diff;
            best = step;
        }
    }
    return bestDiff <= SNAP_EPSILON ? best : dpr;
};

const readReportedDpr = () => Number(window.devicePixelRatio) || 1;

const getNativeDpr = () => {
    const reported = readReportedDpr();

    if (appliedZoom === 1) {
        return snapDpr(reported);
    }

    if (zoomAffectsDpr === false) {
        return snapDpr(reported);
    }

    // zoomAffectsDpr true, or unknown while zoom is on — recover
    return snapDpr(reported / appliedZoom);
};

const updateViewportVars = (zoom) => {
    const root = document.documentElement;
    const layoutHeight = window.innerHeight / zoom;
    const layoutWidth = window.innerWidth / zoom;
    root.style.setProperty("--app-vh", `${layoutHeight}px`);
    root.style.setProperty("--app-vw", `${layoutWidth}px`);
    root.style.minHeight = `${layoutHeight}px`;
    if (document.body) {
        document.body.style.minHeight = `${layoutHeight}px`;
    }
};

const targetZoomForDpr = (nativeDpr) => {
    const isRetinaInteger = Number.isInteger(nativeDpr) && nativeDpr >= 2;
    // Compensate zoom-out (75%/80%) and zoom-in (125%/150%) the same way
    const offNominal = Math.abs(nativeDpr - 1) > 0.03;
    const shouldCompensate =
        !isRetinaInteger && offNominal && nativeDpr >= 0.5 && nativeDpr < 2.5;
    return {
        shouldCompensate,
        zoom: shouldCompensate ? 1 / nativeDpr : 1,
        nativeDpr,
    };
};

const applyDisplayScaleCompensation = () => {
    const root = document.documentElement;
    const { shouldCompensate, zoom, nativeDpr } = targetZoomForDpr(getNativeDpr());

    // Same effective scale — only refresh viewport size (no zoom write → no jump)
    if (Math.abs(zoom - appliedZoom) < 0.001) {
        updateViewportVars(appliedZoom);
        return;
    }

    const reportedBefore = readReportedDpr();
    root.style.zoom = String(zoom);
    appliedZoom = zoom;
    root.setAttribute("data-display-scale", shouldCompensate ? String(nativeDpr) : "1");
    root.setAttribute("data-app-zoom", String(zoom));
    updateViewportVars(zoom);

    // One-time calibration only (never a second zoom write — that caused jumps)
    if (zoomAffectsDpr === null && zoom !== 1) {
        const reportedAfter = readReportedDpr();
        zoomAffectsDpr =
            Math.abs(reportedAfter - reportedBefore) > 0.05 ||
            Math.abs(reportedAfter - 1) < 0.1;
    }
};

// Coalesce bursty Chrome zoom/resize events into one pre-paint apply
const scheduleDisplayScaleCompensation = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
        rafId = 0;
        applyDisplayScaleCompensation();
    });
};

const _initDisplayScaleCompensation = () => {
    applyDisplayScaleCompensation();
    window.addEventListener("resize", scheduleDisplayScaleCompensation);
    window.visualViewport?.addEventListener("resize", scheduleDisplayScaleCompensation);
};

/** Current css zoom on html (1 when not compensating). Used by portals / charts. */
const getAppliedZoom = () => appliedZoom;

export {
    _initDisplayScaleCompensation,
    applyDisplayScaleCompensation,
    getAppliedZoom,
};
